import { useState } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  useTheme
} from "@mui/material";

import ReviewTableShell from "../review-table/ReviewTableShell";
import CsvPager from "./CsvPager";
import CsvLineNotes from "./CsvLineNotes";
import {
  LineActions,
  StatusCell,
  ValueCell,
  VendorCell
} from "./SmartUploadReviewRow";

import {
  CSV_PAGE_SIZE,
  SHIPPING_MODE_OPTIONS,
  csvCategoryFor,
  csvDescriptionFor,
  csvFactoryCodeFor,
  csvLineKeyFor,
  csvOfficeSubcategoryExtraFor,
  csvProductNameFor,
  csvRouteFor,
  csvSupplierNameFor,
  csvUsageTypeFor,
  formatCsvDate,
  formatCsvTons
} from "./csvReviewFormat";
import { reasonCopy } from "./reviewLineGates";
import { useCsvLinePages } from "./useCsvReview";
import { useSmartUploadLineDraft } from "./useSmartUploadLineDraft";
import {
  retrySmartUploadDocumentFiling,
  updateSmartUploadLines
} from "../../../helpers/smartUpload";
import { ATTENTION_REASONS } from "../../../helpers/smartUploadCsvAttention";

const noPendingTracking = () => null;

const GROUP_REASON_COPY = {
  [ATTENTION_REASONS.possibleDuplicate]: (count) =>
    `${count} ${
      count === 1 ? "line looks" : "lines look"
    } already added — skip them or keep them.`,
  [ATTENTION_REASONS.vendorMatchUnconfirmed]: (
    count,
    { vendorName, vendorMatch }
  ) =>
    `We matched “${vendorName || "this vendor"}” to ${
      vendorMatch?.name || "an existing vendor"
    } on ${count} ${count === 1 ? "line" : "lines"} — is that right?`,
  [ATTENTION_REASONS.noVendor]: (count) =>
    `${count} ${count === 1 ? "line has" : "lines have"} no vendor — add one.`,
  [ATTENTION_REASONS.vendorFactorMissing]: (count, { vendorName }) =>
    `We couldn't find an emission factor for ${
      vendorName || "this vendor"
    } (${count} ${count === 1 ? "line" : "lines"}) — pick a different vendor.`,
  [ATTENTION_REASONS.vendorLookupFailed]: (count, { vendorName }) =>
    `We couldn't look up ${vendorName || "the vendor"} on ${count} ${
      count === 1 ? "line" : "lines"
    } — pick or enter it.`,
  [ATTENTION_REASONS.bomNewSupplier]: (count, { vendorName }) =>
    `“${vendorName || "This supplier"}” isn't on file yet (${count} ${
      count === 1 ? "line" : "lines"
    }) — pick an existing supplier or confirm creating it.`,
  [ATTENTION_REASONS.bomSupplierLookupFailed]: (count, { vendorName }) =>
    `We couldn't look up ${vendorName || "this supplier"} on ${count} ${
      count === 1 ? "line" : "lines"
    } — pick or enter one.`,
  [ATTENTION_REASONS.bomSupplierMatchUnconfirmed]: (count, { vendorName }) =>
    `We matched “${vendorName || "this supplier"}” to an existing supplier on ${count} ${
      count === 1 ? "line" : "lines"
    } — is that right?`
};

const VendorPicker = ({ vendorOptions, onPick, disabled, buttonLabel }) => {
  const { palette } = useTheme();
  const [selected, setSelected] = useState(null);
  const [typedName, setTypedName] = useState("");

  const fields = selected
    ? { vendor_relationship_id_final: selected.id }
    : { vendor_name_final: typedName.trim() };
  const canPick = !!selected || !!typedName.trim();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Autocomplete
        freeSolo
        size="small"
        options={vendorOptions}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        value={selected}
        inputValue={selected ? selected.name : typedName}
        onChange={(event, option) =>
          setSelected(option && typeof option === "object" ? option : null)
        }
        onInputChange={(event, text, reason) => {
          if (reason !== "input") {
            return null;
          }
          setSelected(null);
          return setTypedName(text);
        }}
        renderInput={(params) => (
          <TextField {...params} label="Vendor" sx={{ minWidth: 240 }} />
        )}
      />
      <Button
        size="small"
        variant="contained"
        color="secondary"
        sx={{ color: palette.common.white }}
        disabled={disabled || !canPick}
        onClick={() => onPick(fields)}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
};

const VendorMatchActions = ({ group, vendorOptions, onApply, disabled }) => {
  const [isPicking, setIsPicking] = useState(false);
  const { vendorMatch, vendorName } = group;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {vendorMatch?.vendorId && (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() => onApply({ vendor_match_accepted: true })}
          >
            Use {vendorMatch.name}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => setIsPicking(!isPicking)}
        >
          Pick another vendor
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() =>
            onApply({
              force_new_vendor: true,
              ...(vendorName ? { vendor_name_final: vendorName } : {})
            })
          }
        >
          Create new vendor
        </Button>
      </Box>
      {isPicking && (
        <VendorPicker
          vendorOptions={vendorOptions}
          disabled={disabled}
          onPick={onApply}
          buttonLabel="Use this vendor"
        />
      )}
    </Box>
  );
};

// bom_new_supplier / bom_supplier_lookup_failed / bom_supplier_match_unconfirmed
// (ticket DlAT2n1ITtjB6BA9ntuJ): a reviewer either picks an existing
// vendor-relationship (the same VendorPicker transactions_csv uses — the
// supplier picker reuses vendor_relationship_id_final) or acknowledges the
// line as drafted (supplier_creation_acknowledged — for an unconfirmed fuzzy
// match this means "yes, link it" rather than "create it", so the second
// button's label and the acknowledged name it confirms both vary by reason
// instead of always claiming a brand-new supplier is being created).
// A failed lookup needs no separate "retry lookup" endpoint: commit
// (commitBomProductLine.js) re-runs supplier match-or-create from scratch
// and never trusts the draft's lookup, so acknowledging a lookup_failed line
// *is* the retry -- it matches an existing supplier first and only creates
// one when nothing matches, which is what its label says.
//
// A typed (freeSolo) name is a dead end on its own -- hasBomSupplierChoice
// only resolves on vendor_relationship_id_final or
// supplier_creation_acknowledged, and a typed name alone sets neither, so
// typing a name here also acknowledges it in the same update (finding #2,
// PR review ticket DlAT2n1ITtjB6BA9ntuJ). Making hasBomSupplierChoice accept
// vendor_name_final directly was rejected: buildMigratedCsvLine.js already
// pre-fills vendor_name_final with the raw CSV supplier name at draft time,
// so that would have made the new-supplier attention reason never fire.
const bomAcknowledgeLabelFor = ({ vendorName, reason }) => {
  const labelsByReason = {
    [ATTENTION_REASONS.bomSupplierMatchUnconfirmed]: `Use “${vendorName || "the matched supplier"}”`,
    [ATTENTION_REASONS.bomSupplierLookupFailed]: `Match or create “${vendorName || "this supplier"}” on confirm`
  };
  return labelsByReason[reason] || `Create “${vendorName || "this supplier"}”`;
};

const BomSupplierActions = ({ group, vendorOptions, onApply, disabled }) => {
  const [isPicking, setIsPicking] = useState(false);
  const acknowledgeLabel = bomAcknowledgeLabelFor(group);

  const onPickOrType = (fields) =>
    onApply(
      fields.vendor_relationship_id_final
        ? fields
        : { ...fields, supplier_creation_acknowledged: true }
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => setIsPicking(!isPicking)}
        >
          Pick existing supplier
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => onApply({ supplier_creation_acknowledged: true })}
        >
          {acknowledgeLabel}
        </Button>
      </Box>
      {isPicking && (
        <VendorPicker
          vendorOptions={vendorOptions}
          disabled={disabled}
          onPick={onPickOrType}
          buttonLabel="Use this supplier"
        />
      )}
    </Box>
  );
};

const DuplicateActions = ({ onApply, disabled }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
    <Button
      size="small"
      variant="outlined"
      disabled={disabled}
      onClick={() => onApply({ duplicate_resolution: "skip" })}
    >
      Skip all
    </Button>
    <Button
      size="small"
      variant="outlined"
      disabled={disabled}
      onClick={() => onApply({ duplicate_resolution: "keep" })}
    >
      Keep all
    </Button>
  </Box>
);

const GroupActions = ({ group, vendorOptions, onApply, disabled }) => {
  const actionsByReason = {
    [ATTENTION_REASONS.vendorMatchUnconfirmed]: (
      <VendorMatchActions
        group={group}
        vendorOptions={vendorOptions}
        onApply={onApply}
        disabled={disabled}
      />
    ),
    [ATTENTION_REASONS.possibleDuplicate]: (
      <DuplicateActions onApply={onApply} disabled={disabled} />
    ),
    [ATTENTION_REASONS.noVendor]: (
      <VendorPicker
        vendorOptions={vendorOptions}
        disabled={disabled}
        onPick={onApply}
        buttonLabel="Use for all"
      />
    ),
    [ATTENTION_REASONS.vendorLookupFailed]: (
      <VendorPicker
        vendorOptions={vendorOptions}
        disabled={disabled}
        onPick={onApply}
        buttonLabel="Use for all"
      />
    ),
    [ATTENTION_REASONS.vendorFactorMissing]: (
      <VendorPicker
        vendorOptions={vendorOptions}
        disabled={disabled}
        onPick={onApply}
        buttonLabel="Use for all"
      />
    ),
    [ATTENTION_REASONS.bomNewSupplier]: (
      <BomSupplierActions
        group={group}
        vendorOptions={vendorOptions}
        onApply={onApply}
        disabled={disabled}
      />
    ),
    [ATTENTION_REASONS.bomSupplierLookupFailed]: (
      <BomSupplierActions
        group={group}
        vendorOptions={vendorOptions}
        onApply={onApply}
        disabled={disabled}
      />
    ),
    [ATTENTION_REASONS.bomSupplierMatchUnconfirmed]: (
      <BomSupplierActions
        group={group}
        vendorOptions={vendorOptions}
        onApply={onApply}
        disabled={disabled}
      />
    )
  };
  return actionsByReason[group.reason] || null;
};

const ModeCell = ({ draft }) => {
  const { draftLine: line, saveNow } = draft;
  return (
    <TextField
      select
      size="small"
      value={line.shipping_mode_final || ""}
      onChange={({ target: { value } }) =>
        value && saveNow({ shipping_mode_final: value })
      }
      SelectProps={{ native: true }}
      inputProps={{ "aria-label": "Mode" }}
    >
      <option value="">Choose a mode</option>
      {SHIPPING_MODE_OPTIONS.map(({ value, name }) => (
        <option key={value} value={value}>
          {name}
        </option>
      ))}
    </TextField>
  );
};

const NOTES_AND_STATUS_COLUMNS = ({ closedYears, commitResults }) => [
  {
    id: "notes",
    label: "Notes",
    render: (line, index, draft) => (
      <CsvLineNotes line={draft.draftLine} closedYears={closedYears} />
    )
  },
  {
    id: "status",
    label: "Status",
    render: (line, index, draft) => (
      <StatusCell
        line={draft.draftLine}
        closedYears={closedYears}
        saveError={draft.saveError}
        commitResult={commitResults[csvLineKeyFor(line)]}
      />
    )
  }
];

// One config per family instead of a growing set of isShipping-style
// booleans (ticket DlAT2n1ITtjB6BA9ntuJ) — a fourth family adds a fourth
// entry here, not a fourth boolean threaded through every branch below.
const LINE_COLUMNS_BY_FAMILY = {
  shipping_transactions: ({ closedYears, commitResults }) => [
    {
      id: "date",
      label: "Ship date",
      render: (line, index, draft) =>
        formatCsvDate(draft.draftLine.period_end_final)
    },
    { id: "route", label: "From → To", render: (line) => csvRouteFor(line) },
    {
      id: "quantity",
      label: "Weight",
      render: (line, index, draft) => (
        <ValueCell
          draft={draft}
          field="quantity_final"
          confidenceField="quantity"
          type="number"
          label="Weight"
        />
      )
    },
    {
      id: "mode",
      label: "Mode",
      render: (line, index, draft) => <ModeCell draft={draft} />
    },
    ...NOTES_AND_STATUS_COLUMNS({ closedYears, commitResults })
  ],
  bom_products_csv: ({ closedYears, commitResults }) => [
    {
      id: "product",
      label: "Product",
      render: (line) => csvProductNameFor(line)
    },
    {
      id: "supplier",
      label: "Supplier",
      render: (line) => csvSupplierNameFor(line)
    },
    {
      id: "factory",
      label: "Factory",
      render: (line) => csvFactoryCodeFor(line)
    },
    {
      id: "category",
      label: "Category",
      render: (line) => csvCategoryFor(line)
    },
    ...NOTES_AND_STATUS_COLUMNS({ closedYears, commitResults })
  ],
  utilities_csv: ({ closedYears, commitResults }) => [
    {
      id: "date",
      label: "Date",
      render: (line, index, draft) =>
        formatCsvDate(draft.draftLine.period_end_final)
    },
    {
      id: "usageType",
      label: "Usage type",
      render: (line) => csvUsageTypeFor(line)
    },
    {
      id: "quantity",
      label: "Quantity",
      render: (line, index, draft) => (
        <ValueCell
          draft={draft}
          field="quantity_final"
          confidenceField="quantity"
          type="number"
          label="Quantity"
        />
      )
    },
    {
      id: "unit",
      label: "Unit",
      render: (line, index, draft) => (
        <ValueCell
          draft={draft}
          field="unit_final"
          confidenceField="unit"
          label="Unit"
        />
      )
    },
    {
      id: "tons",
      label: "tCO2e",
      render: (line) => formatCsvTons(line.draft_tons_co2e)
    },
    ...NOTES_AND_STATUS_COLUMNS({ closedYears, commitResults })
  ],
  office_csv: ({ closedYears, commitResults }) => [
    {
      id: "date",
      label: "Date",
      render: (line, index, draft) =>
        formatCsvDate(draft.draftLine.period_end_final)
    },
    {
      id: "subcategory",
      label: "Subcategory",
      render: (line) => line.subcategory_final || "—"
    },
    {
      id: "quantity",
      label: "Quantity",
      render: (line, index, draft) => (
        <ValueCell
          draft={draft}
          field="quantity_final"
          confidenceField="quantity"
          type="number"
          label="Quantity"
        />
      )
    },
    {
      id: "unit",
      label: "Unit",
      render: (line, index, draft) => (
        <ValueCell
          draft={draft}
          field="unit_final"
          confidenceField="unit"
          label="Unit"
        />
      )
    },
    {
      id: "subcategoryExtra",
      label: "Details",
      render: (line) => csvOfficeSubcategoryExtraFor(line)
    },
    {
      id: "tons",
      label: "tCO2e",
      render: (line) => formatCsvTons(line.draft_tons_co2e)
    },
    ...NOTES_AND_STATUS_COLUMNS({ closedYears, commitResults })
  ]
};

const DEFAULT_LINE_COLUMNS = ({
  vendorOptions,
  closedYears,
  commitResults
}) => [
  {
    id: "date",
    label: "Date",
    render: (line, index, draft) =>
      formatCsvDate(draft.draftLine.period_end_final)
  },
  {
    id: "description",
    label: "Description",
    render: (line) => csvDescriptionFor(line)
  },
  {
    id: "quantity",
    label: "Amount",
    render: (line, index, draft) => (
      <ValueCell
        draft={draft}
        field="quantity_final"
        confidenceField="quantity"
        type="number"
        label="Amount"
      />
    )
  },
  {
    id: "vendor",
    label: "Vendor",
    render: (line, index, draft) => (
      <VendorCell draft={draft} vendorOptions={vendorOptions} />
    )
  },
  ...NOTES_AND_STATUS_COLUMNS({ closedYears, commitResults })
];

const lineColumns = ({
  documentFamily,
  vendorOptions,
  closedYears,
  commitResults
}) =>
  (LINE_COLUMNS_BY_FAMILY[documentFamily] || DEFAULT_LINE_COLUMNS)({
    vendorOptions,
    closedYears,
    commitResults
  });

// The lines of one attention group, a page at a time, each editable on its
// own with the same per-line cells and actions every other review line uses
// (an individual change marks the line as overridden, so a later group
// decision leaves it alone).
const AttentionGroupLines = ({
  group,
  companyId,
  taskDocId,
  documentFamily,
  vendorOptions,
  closedYears,
  commitResults,
  refreshKey,
  isCommitting,
  onConfirmLine,
  onLineChanged
}) => {
  const pages = useCsvLinePages({
    companyId,
    taskDocId,
    filter: "attention",
    groupKey: group.groupKey,
    refreshKey
  });
  const { lines } = pages;

  const saveFieldsFor = (line) => async (fields) => {
    const response = await updateSmartUploadLines({
      companyId,
      taskDocId,
      rowId: line.rowId,
      updates: [{ lineId: line.id, fields }]
    });
    const rejection = (response?.rejected || [])[0];
    onLineChanged();
    return rejection
      ? {
          error: true,
          message: rejection.message || reasonCopy(rejection.reason)
        }
      : response;
  };

  const useLineContext = (line) =>
    useSmartUploadLineDraft({
      line,
      documentFamily,
      lineKey: csvLineKeyFor(line),
      saveFields: saveFieldsFor(line),
      onPendingChange: noPendingTracking
    });

  return (
    <ReviewTableShell
      title="Lines in this group"
      subtitle={`${group.count} ${group.count === 1 ? "line" : "lines"}`}
      defaultExpanded
      banners={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
          <CsvPager pages={pages} pageSize={CSV_PAGE_SIZE} />
          {pages.error && <Alert severity="error">{pages.error}</Alert>}
        </Box>
      }
      columns={lineColumns({
        documentFamily,
        vendorOptions,
        closedYears,
        commitResults
      })}
      lines={lines}
      getLineKey={csvLineKeyFor}
      useLineContext={useLineContext}
      actionsLabel="Actions"
      renderLineActions={(line, index, draft) => (
        <LineActions
          draft={draft}
          isCommitting={isCommitting}
          onConfirm={() =>
            onConfirmLine({ rowId: line.rowId, lineId: line.id })
          }
          onRetryFiling={() =>
            retrySmartUploadDocumentFiling({
              companyId,
              taskDocId,
              rowId: line.rowId,
              lineId: line.id
            })
          }
        />
      )}
      body={
        (!lines.length &&
          (pages.isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Nothing left to review on this page.
            </Typography>
          ))) ||
        undefined
      }
    />
  );
};

const AttentionGroupCard = ({ group, onGroupUpdate, ...lineProps }) => {
  const { palette } = useTheme();
  const [isApplying, setIsApplying] = useState(false);
  const [isShowingLines, setIsShowingLines] = useState(false);
  const { reason, label, vendorName, count } = group;
  const describe = GROUP_REASON_COPY[reason];

  const applyToGroup = async (fields) => {
    setIsApplying(true);
    await onGroupUpdate(group.groupKey, fields);
    return setIsApplying(false);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${palette.warning.light}`,
        borderRadius: 1,
        p: 1.5,
        mb: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="subtitle1">
          {vendorName && reason !== ATTENTION_REASONS.possibleDuplicate
            ? `${label} · ${vendorName}`
            : label}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          label={`${count} ${count === 1 ? "line" : "lines"}`}
        />
        {isApplying && <CircularProgress size={16} />}
      </Box>
      {describe && (
        <Typography variant="body2" color="textSecondary">
          {describe(count, group)}
        </Typography>
      )}
      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
      >
        <GroupActions
          group={group}
          vendorOptions={lineProps.vendorOptions}
          onApply={applyToGroup}
          disabled={isApplying}
        />
        <Button size="small" onClick={() => setIsShowingLines(!isShowingLines)}>
          {isShowingLines ? "Hide lines" : "Review individually"}
        </Button>
      </Box>
      {isShowingLines && <AttentionGroupLines group={group} {...lineProps} />}
    </Box>
  );
};

const AttentionLoadProblem = ({ error, attentionCount, onRetry }) => (
  <Alert
    severity={error ? "error" : "warning"}
    sx={{ my: 1 }}
    action={
      <Button size="small" onClick={onRetry}>
        Retry
      </Button>
    }
  >
    {error ||
      `${attentionCount} ${
        attentionCount === 1 ? "line needs" : "lines need"
      } your attention, but we couldn't list them yet.`}
  </Alert>
);

// Review by exception: only the lines a person must decide on, grouped so
// one decision covers every line that shares it. A failed or empty groups
// read never reads as "nothing needs attention" while the summary says
// otherwise.
const CsvAttentionGroups = ({
  groups,
  truncated,
  error,
  attentionCount,
  onRetry,
  ...cardProps
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6">Needs your attention</Typography>
    {(!!error || !groups.length) && (
      <AttentionLoadProblem
        error={error}
        attentionCount={attentionCount}
        onRetry={onRetry}
      />
    )}
    {truncated && (
      <Alert severity="info" sx={{ my: 1 }}>
        Not every line needing attention is grouped here yet — resolve these
        and more will appear.
      </Alert>
    )}
    {groups.map((group) => (
      <AttentionGroupCard key={group.groupKey} group={group} {...cardProps} />
    ))}
  </Box>
);

export default CsvAttentionGroups;
// Exported for direct unit testing of the typed-name-then-apply flow
// (finding #2, ticket DlAT2n1ITtjB6BA9ntuJ) without standing up the full
// attention-group data pipeline the default export depends on.
export { BomSupplierActions };
